<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Trips Report</h1>

                <v-menu>
                    <template v-slot:activator="{ props }">
                        <v-btn :loading="downloading" color="black" prepend-icon="mdi-download" v-bind="props">Export
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item>
                            <v-list-item-title class="cursor-pointer" @click="fetchTrips('pdf')">PDF</v-list-item-title>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-title class="cursor-pointer" @click="fetchTrips('csv')">CSV</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="3" sm="6">
                <company-select v-model="formData['company']" :items="companies" density="compact" label="Select Company"
                          @update:model-value="resetForm();"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-select v-model="formData['asset']" :items="assets" clearable multiple
                          density="compact" item-title="label" item-value="id" label="Select Asset"
                          @update:model-value="formData['page']=1;fetchTrips()"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-select v-model="formData['driver']" :items="drivers" clearable
                          density="compact" :item-title="d => `${d.first_name || ''} ${d.last_name || ''}`.trim()" item-value="id" label="Select Driver"
                          @update:model-value="formData['page']=1;fetchTrips()"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-text-field v-model="formData['start_date']" density="compact" label="Start Date" type="date"
                              @update:model-value="formData['page']=1;fetchTrips()"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-text-field v-model="formData['end_date']" density="compact" label="End Date" type="date"
                              @update:model-value="formData['page']=1;fetchTrips()"/>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th style="width: 40px;"></th>
                        <th class="text-center" style="width: 50px;">Score</th>
                        <th-btn :sortedColumn="formData['sort']" column="asset" sortable title="Asset" @sort="sort"/>
                        <th-btn :sortedColumn="formData['sort']" column="first_name" sortable title="Driver"
                                @sort="sort"/>
                        <th-btn :sortedColumn="formData['sort']" column="trip_type" sortable title="Trip Type"
                                @sort="sort"/>
                        <th-btn :sortedColumn="formData['sort']" column="duration" sortable title="Duration"
                                @sort="sort"/>
                        <th>Idle Percentage</th>
                        <th-btn :sortedColumn="formData['sort']" column="distance_km" sortable title="Distance"
                                @sort="sort"/>
                        <th-btn :sortedColumn="formData['sort']" column="start_time" sortable title="Start Time"
                                @sort="sort"/>
                        <th-btn :sortedColumn="formData['sort']" column="end_time" sortable title="End Time"
                                @sort="sort"/>
                        <th-btn :sortedColumn="formData['sort']" column="start_location_text" sortable
                                title="Start Location" @sort="sort"/>
                        <th-btn :sortedColumn="formData['sort']" column="end_location_text" sortable
                                title="End Location"
                                @sort="sort"/>
                    </tr>
                    </thead>
                    <tbody>
                    <template v-for="(trip, index) in trips" v-if="trips.length && !loading" :key="trip.id">
                        <tr class="text-left cursor-pointer" :data-trip-row-id="trip.id" @click="toggleTripExpand(index)">
                            <td class="pa-0 px-1">
                                <v-btn :icon="`mdi-${expandedTrip === index ? 'minus' : 'plus'}`"
                                       density="compact" size="x-small" variant="text"/>
                            </td>
                            <td class="text-center">
                                <v-chip v-if="hasTripScore(trip)"
                                        :color="tripScoreColor(trip.trip_score)"
                                        size="x-small" variant="tonal"
                                        class="font-weight-bold cursor-pointer"
                                        @click.stop="selectedScoreTrip = trip; tripScoreDialogSwitch = true">
                                    {{ formatTripScore(trip.trip_score) }}
                                </v-chip>
                                <span v-else class="text-grey text-caption">-</span>
                            </td>
                            <td>{{ trip.asset }}</td>
                            <td>{{ trip.first_name ? (trip.first_name + ' ' + trip.last_name) : '' }}</td>
                            <td>{{
                                    trip.trip_type === 0 ? 'Unclassified' : (trip.trip_type === 1 ? 'Displaced' : 'Non-displacement')
                                }}
                            </td>
                            <td>{{ secToHours(trip.duration) }}</td>
                            <td>
                                {{
                                    (trip.duration > 1 && trip.idle_time_total_sec > 1) ? parseFloat((trip.idle_time_total_sec / trip.duration) * 100).toFixed(2) + '%' : ''
                                }}
                            </td>
                            <td>{{ strOperations().unitifyDistance(trip.distance_km, 'distance', 'km') }}</td>
                            <td class="text-body-2">{{ formatDateTime(trip.start_time) }}</td>
                            <td class="text-body-2">{{ formatDateTime(trip.end_time) }}</td>
                            <td :title="trip.start_location_text" class="text-body-2">{{
                                    trip.start_location_text?.length > 20 ? (trip.start_location_text.substr(0, 20) + '...') : trip.start_location_text
                                }}
                            </td>
                            <td :title="trip.end_location_text" class="text-body-2">{{
                                    trip.end_location_text?.length > 20 ? (trip.end_location_text.substr(0, 20) + '...') : trip.end_location_text
                                }}
                            </td>
                        </tr>
                        <!-- Expanded trip detail row -->
                        <tr v-if="expandedTrip === index">
                            <td :colspan="12" class="pa-0">
                                <div class="trip-expanded-content pa-1">
                                    <v-row class="align-stretch">
                                        <v-col cols="12" md="4">
                                            <v-table density="compact" class="trip-stats-table">
                                                <tbody>
                                                <tr v-if="trip.first_name">
                                                    <td class="font-weight-bold">Driver</td>
                                                    <td>{{ trip.first_name + ' ' + (trip.last_name || '') }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">Asset</td>
                                                    <td>{{ trip.asset }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">Trip Type</td>
                                                    <td>{{ trip.trip_type === 0 ? 'Unclassified' : (trip.trip_type === 1 ? 'Displaced' : 'Non-displacement') }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">Duration</td>
                                                    <td>{{ secToHours(trip.duration) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">Distance</td>
                                                    <td>{{ strOperations().unitifyDistance(trip.distance_km, 'distance', 'km') }}</td>
                                                </tr>
                                                <tr v-if="trip.duration > 1 && trip.idle_time_total_sec > 1">
                                                    <td class="font-weight-bold">Idle %</td>
                                                    <td>{{ parseFloat((trip.idle_time_total_sec / trip.duration) * 100).toFixed(2) }}%</td>
                                                </tr>
                                                <tr v-if="trip.idle_time_total_sec > 0">
                                                    <td class="font-weight-bold">Total Idle</td>
                                                    <td>{{ secToHours(trip.idle_time_total_sec) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">Start Time</td>
                                                    <td>{{ formatDateTime(trip.start_time) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">End Time</td>
                                                    <td>{{ formatDateTime(trip.end_time) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">From</td>
                                                    <td>
                                                        <span>{{ trip.start_location_text || `${trip.start_lat}, ${trip.start_lon}` }}</span>
                                                        <a v-if="trip.start_lat && trip.start_lon"
                                                           :href="`https://www.google.com/maps?q=${trip.start_lat},${trip.start_lon}`"
                                                           target="_blank" class="ms-1">
                                                            <v-icon size="x-small" icon="mdi-open-in-new"/>
                                                        </a>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="font-weight-bold">To</td>
                                                    <td>
                                                        <span>{{ trip.end_location_text || `${trip.end_lat}, ${trip.end_lon}` }}</span>
                                                        <a v-if="trip.end_lat && trip.end_lon"
                                                           :href="`https://www.google.com/maps?q=${trip.end_lat},${trip.end_lon}`"
                                                           target="_blank" class="ms-1">
                                                            <v-icon size="x-small" icon="mdi-open-in-new"/>
                                                        </a>
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </v-table>
                                        </v-col>
                                        <v-col cols="12" md="8" class="d-flex">
                                            <div class="flex-grow-1" style="min-height: 200px;">
                                                <trip-route-map
                                                    :selected-device="getTripDevice(trip)"
                                                    :selected-trip="trip"
                                                />
                                            </div>
                                        </v-col>
                                    </v-row>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <tr v-else>
                        <td v-if="loading" colspan="12">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="12">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination v-model="formData['page']" :length="totalPages" class="my-4" density="compact"
                              @update:model-value="fetchTrips(false)"/>
            </v-col>
        </v-row>
    </v-container>
    <TripScoreDialog
        v-if="tripScoreDialogSwitch"
        v-model="tripScoreDialogSwitch"
        :trip="selectedScoreTrip"
        @view-on-map="onTripScoreViewOnMap"
    />
</template>

<script setup>
import {onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useOnboardingStore} from "@/stores/onboardingStore";
import router from "@/router";
import ThBtn from "@/components/globals/th-btn.vue";
import TripRouteMap from "@/components/cards/TripRouteMap.vue";
import {strOperations} from "@/composables/globals";
import {formatDateTime} from '@/composables/useTimezone';
import {useTripScore} from '@/composables/useTripScore';
import TripScoreDialog from "@/components/dialogs/trips/TripScoreDialog.vue";
import {useReportExport} from "@/composables/useReportExport";
import CompanySelect from "@/components/globals/company-select.vue";

const {handleDownloadResponse} = useReportExport();

const trips = ref([]);
const expandedTrip = ref(null);
const {user} = useAuthStore();
const assets = ref([]);
const drivers = ref([]);
const companies = ref([]);
const totalPages = ref(1);
const loading = ref(false);
const downloading = ref(false);
const {showSnackbar} = useSnackbarStore();
const formData = reactive({
    page: 1,
    asset: [],
    driver: null,
    end_date: null,
    start_date: null,
    company: ref(user.type !== 'admin' ? user.company.id : null),
    per_page: 25,
})

onMounted(async () => {
    // Track onboarding milestone
    useOnboardingStore().markTripsViewed();

    if (user.type === 'admin') {
        await fetchCompaniesList().then(() => {
            if (parseInt(router.currentRoute.value.params.company)) {
                formData['company'] = parseInt(router.currentRoute.value.params.company);
            }
        });
    } else {
        await fetchAssetList();
        fetchDriversList();
    }

    if (parseInt(router.currentRoute.value.params.asset)) {
        if (!assets.value.length) await fetchAssetList();
        formData['asset'] = [parseInt(router.currentRoute.value.params.asset)];
    }
    if (router.currentRoute.value.params.type) {
        switch (router.currentRoute.value.params.type) {
            case 'daily':
                formData['start_date'] = (new Date()).toISOString().split('T')[0];
                break;
            case 'monthly':
                break;
        }
    }

    if (formData['company']) {
        fetchTrips();
    }
});

const resetForm = () => {
    formData['page'] = 1;
    formData['asset'] = [];
    formData['driver'] = null;
    formData['end_date'] = null;
    formData['start_date'] = null;
    fetchTrips();
    fetchAssetList();
    fetchDriversList();
}
const secToHours = (seconds) => {
    if (seconds < 1) {
        return ''
    }
    return new Date(seconds * 1000).toISOString().slice(11, 19)
}

const sort = (sorting) => {
    formData['sort'] = sorting.column;
    formData['order'] = sorting.order;
    fetchTrips();
}
const fetchCompaniesList = () => {
    return send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchAssetList = () => {
    return send('get', `assets/list`, {company: formData['company']})
        .then((response) => {
            assets.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Assets List could not be loaded', 'error')
        })
}
const fetchDriversList = () => {
    send('get', `companies/drivers?list=true`, {company: formData['company']})
        .then((response) => {
            drivers.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Drivers List could not be loaded', 'error')
        })
}
const {tripScoreColor, formatTripScore, hasTripScore} = useTripScore();
const tripScoreDialogSwitch = ref(false);
const selectedScoreTrip = ref(null);
const toggleTripExpand = (index) => {
    expandedTrip.value = expandedTrip.value === index ? null : index;
};

// Recap dialog "View events on map" handler - expands the trip row
// (which renders the embedded TripRouteMap) and scrolls it into view.
const onTripScoreViewOnMap = (trip) => {
    if (!trip) return;
    const idx = trips.value.findIndex((t) => t.id === trip.id);
    if (idx >= 0) {
        expandedTrip.value = idx;
        setTimeout(() => {
            const el = document.querySelector(`[data-trip-row-id="${trip.id}"]`);
            if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
        }, 50);
    }
};

const getTripDevice = (trip) => ({
    asset_id: trip.asset_id,
    company: trip.company_id,
});

const fetchTrips = (download = false) => {
    if (formData['company']) {
        expandedTrip.value = null;
        let data = formData;
        if (download) {
            data['type'] = download;
            data['download'] = true;
            downloading.value = true;
        } else {
            loading.value = true;
        }
        // responseType:'text' for downloads so axios doesn't try to JSON-parse the
        // gzip CSV / base64 PDF body.
        send('post', `reports/trips`, data, download ? {responseType: 'text'} : {})
            .then((response) => {
                if (download) {
                    handleDownloadResponse(response, `trips-report.${data['type'].toLowerCase()}`);
                } else {
                    trips.value = response.data.data.data;
                    totalPages.value = response.data.data.last_page;
                    formData['page'] = response.data.data.current_page;
                }
                formData['download'] = false;
            })
            .catch((error) => {
                console.log(error)
                showSnackbar('Trips report could not be loaded', 'error')
            }).finally(() => {
                loading.value = false;
                downloading.value = false;
            });
    }
}

</script>

<style>
.trip-expanded-content {
    background-color: #fafafa;
    border-top: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
}

.trip-stats-table td {
    padding: 1px 4px !important;
    font-size: 0.8rem;
    height: auto !important;
}

.trip-expanded-content :deep(.half-card) {
    height: 100% !important;
    display: flex;
    flex-direction: column;
}

.trip-expanded-content :deep(.half-card .v-card-title) {
    padding: 4px 8px !important;
}

.trip-expanded-content :deep(.half-card .v-card-title h3) {
    font-size: 0.85rem;
}

.trip-expanded-content :deep(.half-card .v-card-text) {
    height: auto !important;
    flex: 1;
}
</style>
